import type { ReactNode } from 'react';
import { ConfirmDialog } from '@/shared/ui/confirm-dialog';
import type { PendingConfirmation } from '@/shared/app-stepper-form/hooks/use-confirmation';
import type { AppStepperFormProps } from '@/shared/app-stepper-form/types';

/**
 * The two confirmations the stepper can raise, as one part.
 *
 * WHY THEY LIVE TOGETHER
 * ----------------------
 * They are the same mechanism with different words, and they must never both be
 * open. Keeping them in one component makes that exclusivity structural: there
 * is one `pending` value, so "discard and publish are both showing" is not a
 * state this file can express.
 *
 * WHY THE DISCARD DIALOG IS CONDITIONAL
 * -------------------------------------
 * It is only rendered when the caller has said there is something to lose.
 * Rendering it unconditionally would put a dialog in the tree that can never
 * open — dead markup that drifts out of sync with the props it was written for.
 */
export function StepperDialogs({
  pending,
  hasUnsavedChanges,
  confirmSubmit,
  isSubmitting,
  onDismiss,
  onConfirmDiscard,
  onConfirmPublish,
}: {
  pending: PendingConfirmation;
  hasUnsavedChanges: boolean;
  confirmSubmit: AppStepperFormProps['confirmSubmit'];
  isSubmitting: boolean;
  onDismiss: () => void;
  onConfirmDiscard: () => void;
  onConfirmPublish: () => void;
}): ReactNode {
  return (
    <>
      {hasUnsavedChanges ? (
        <ConfirmDialog
          open={pending === 'discard'}
          onOpenChange={(open) => {
            if (!open) onDismiss();
          }}
          title="Discard this draft?"
          description="The details you have entered will be lost. Nothing has been saved yet."
          confirmLabel="Discard draft"
          onConfirm={onConfirmDiscard}
        />
      ) : null}

      {confirmSubmit ? (
        <ConfirmDialog
          open={pending === 'publish'}
          onOpenChange={(open) => {
            if (!open) onDismiss();
          }}
          title={confirmSubmit.title}
          description={confirmSubmit.description}
          confirmLabel={confirmSubmit.confirmLabel}
          tone="primary"
          busy={isSubmitting}
          onConfirm={onConfirmPublish}
        />
      ) : null}
    </>
  );
}
